import { api } from "@/lib/api";
import type {
  TagGroup,
  TagGroupVariant,
  TagOption,
  TagOptionVariant,
  TaxonomyStatus,
} from "../schema";

const TAG_GROUPS = "/content/tag-groups";
const TAG_OPTIONS = "/content/tag-options";
const TAG_GROUP_VARIANTS = "/content/tag-groups-variants";
const TAG_OPTION_VARIANTS = "/content/tag-options-variants";

export interface CreateTagGroupRequest {
  name: string;
  type: string;
  description?: string | null;
  approvalRequired?: boolean;
  primaryLanguageCode?: string;
}

export interface UpdateTagGroupRequest {
  name?: string;
  type?: string;
  description?: string | null;
  approvalRequired?: boolean;
  primaryLanguageCode?: string;
  status?: TaxonomyStatus;
  archivedAt?: string | null;
}

export interface CreateTagOptionRequest {
  groupId: string;
  label: string;
  description?: string | null;
  displayOrder?: number;
  primaryLanguageCode?: string;
  parentTagOptionIds?: string[];
}

export interface UpdateTagOptionRequest {
  label?: string;
  description?: string | null;
  displayOrder?: number;
  primaryLanguageCode?: string;
  status?: TaxonomyStatus;
  archivedAt?: string | null;
  parentTagOptionIds?: string[];
}

export interface CreateTagGroupVariantRequest {
  languageCode: string;
  isDefault?: boolean;
  localizedTitle: string;
  localizedDescription?: string | null;
}

export interface UpdateTagGroupVariantRequest {
  languageCode?: string;
  isDefault?: boolean;
  localizedTitle?: string;
  localizedDescription?: string | null;
}

export interface CreateTagOptionVariantRequest {
  languageCode: string;
  isDefault?: boolean;
  localizedLabel: string;
  localizedDescription?: string | null;
}

export interface UpdateTagOptionVariantRequest {
  languageCode?: string;
  isDefault?: boolean;
  localizedLabel?: string;
  localizedDescription?: string | null;
}

export const ContentTagService = {
  async getTagGroups(language?: string): Promise<TagGroup[]> {
    const params = language ? `?language=${language}` : "";
    return api.get<TagGroup[]>(`${TAG_GROUPS}${params}`);
  },

  async getTagOptions(): Promise<TagOption[]> {
    return api.get<TagOption[]>(TAG_OPTIONS);
  },

  async getTags(language: string = "en"): Promise<TagGroup[]> {
    return this.getTagGroups(language);
  },

  async createTagGroup(data: CreateTagGroupRequest): Promise<TagGroup> {
    return api.post<TagGroup>(TAG_GROUPS, data);
  },

  async updateTagGroup(
    id: string,
    data: UpdateTagGroupRequest,
  ): Promise<TagGroup> {
    return api.patch<TagGroup>(`${TAG_GROUPS}/${id}`, data);
  },

  async getTagGroupVariants(groupId: string): Promise<TagGroupVariant[]> {
    return api.get<TagGroupVariant[]>(`${TAG_GROUPS}/${groupId}/variants`);
  },

  async createTagGroupVariant(
    groupId: string,
    data: CreateTagGroupVariantRequest,
  ): Promise<TagGroupVariant> {
    return api.post<TagGroupVariant>(`${TAG_GROUPS}/${groupId}/variants`, data);
  },

  async updateTagGroupVariant(
    id: string,
    data: UpdateTagGroupVariantRequest,
  ): Promise<TagGroupVariant> {
    return api.patch<TagGroupVariant>(`${TAG_GROUP_VARIANTS}/${id}`, data);
  },

  async deleteTagGroupVariant(id: string): Promise<void> {
    return api.delete(`${TAG_GROUP_VARIANTS}/${id}`);
  },

  async createTagOption(data: CreateTagOptionRequest): Promise<TagOption> {
    return api.post<TagOption>(TAG_OPTIONS, data);
  },

  async updateTagOption(
    id: string,
    data: UpdateTagOptionRequest,
  ): Promise<TagOption> {
    return api.patch<TagOption>(`${TAG_OPTIONS}/${id}`, data);
  },

  async getTagOptionVariants(optionId: string): Promise<TagOptionVariant[]> {
    return api.get<TagOptionVariant[]>(`${TAG_OPTIONS}/${optionId}/variants`);
  },

  async createTagOptionVariant(
    optionId: string,
    data: CreateTagOptionVariantRequest,
  ): Promise<TagOptionVariant> {
    return api.post<TagOptionVariant>(
      `${TAG_OPTIONS}/${optionId}/variants`,
      data,
    );
  },

  async updateTagOptionVariant(
    id: string,
    data: UpdateTagOptionVariantRequest,
  ): Promise<TagOptionVariant> {
    return api.patch<TagOptionVariant>(`${TAG_OPTION_VARIANTS}/${id}`, data);
  },

  async deleteTagOptionVariant(id: string): Promise<void> {
    return api.delete(`${TAG_OPTION_VARIANTS}/${id}`);
  },
};
